import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    GetShowcaseItems,
    GetShowcaseCategories,
    GetShowcaseCategoriesLevel1,
    GetShowcases
} from "@/components/api/queries";
import {
    ShowcaseItem,
    ShowcaseCategory,
    ShowcaseCategoryLevel1,
    Showcase
} from "@/components/api/zod-types";
import { useAccumulate } from "@/components/context/AccumulateContext";
import { Button } from "@/components/ui/button";
import { SafeLink } from "@/components/ui/SafeLink";
import { FaPlus, FaMinus } from "react-icons/fa";

function slugify(str: string) {
    return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AllShowcaseItems() {
    // Fetch all data
    const { data: categoriesData } = useQuery({
        queryKey: ["showcasecategories"],
        queryFn: GetShowcaseCategories,
        staleTime: 5 * 60 * 1000,
    });
    const { data: categoriesLevel1Data } = useQuery({
        queryKey: ["showcasecategorieslevel1"],
        queryFn: GetShowcaseCategoriesLevel1,
        staleTime: 5 * 60 * 1000,
    });
    const { data: showcasesData } = useQuery({
        queryKey: ["showcases"],
        queryFn: GetShowcases,
        staleTime: 5 * 60 * 1000,
    });
    const { data: itemsData, isLoading: isLoadingItems } = useQuery({
        queryKey: ["showcaseitems"],
        queryFn: GetShowcaseItems,
        staleTime: 5 * 60 * 1000,
    });

    // Parse data
    const categories: ShowcaseCategory[] = categoriesData?.data?.results || [];
    const categoriesLevel1: ShowcaseCategoryLevel1[] = categoriesLevel1Data?.data?.results || [];
    const showcases: Showcase[] = showcasesData?.data?.results || [];
    const items: ShowcaseItem[] = itemsData?.data?.results || [];

    // Only show level 0 categories
    const level0Categories = categories.filter(cat => cat.categorylevel === "0");
    // Only show level 1 categories
    const level1Categories = categoriesLevel1.filter(l1 => l1.categorylevel === "1");

    // Filter state
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [selectedLevel1, setSelectedLevel1] = useState<string | null>(null);
    const [selectedShowcase, setSelectedShowcase] = useState<string | null>(null);

    // Expand/collapse state for sidebar
    const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
    const [expandedLevel1, setExpandedLevel1] = useState<string[]>([]);

    // Sidebar expand/collapse handlers
    const toggleCategory = (catTag: string) => {
        setExpandedCategories(prev =>
            prev.includes(catTag)
                ? prev.filter(tag => tag !== catTag)
                : [...prev, catTag]
        );
    };
    const toggleLevel1 = (level1Tag: string) => {
        setExpandedLevel1(prev =>
            prev.includes(level1Tag)
                ? prev.filter(tag => tag !== level1Tag)
                : [...prev, level1Tag]
        );
    };

    // Filter logic for items grid
    let displayItems: ShowcaseItem[] = items;
    if (selectedShowcase) {
        displayItems = items.filter(i => slugify(i.showcasename) === slugify(selectedShowcase));
    } else if (selectedLevel1) {
        const showcaseNames = showcases
            .filter(s => slugify(s.showcasecategorytag) === selectedLevel1)
            .map(s => s.showcasename);
        displayItems = items.filter(i => showcaseNames.includes(i.showcasename));
    } else if (selectedCategory) {
        const level1Tags = level1Categories
            .filter(l1 => slugify(l1.categorytag) === selectedCategory)
            .map(l1 => slugify(l1.categorytag));
        const showcaseNames = showcases
            .filter(s => level1Tags.includes(slugify(s.showcasecategorytag)))
            .map(s => s.showcasename);
        displayItems = items.filter(i => showcaseNames.includes(i.showcasename));
    }

    // Accumulate context
    const { setQuoteItems } = useAccumulate();

    return (
        <div className="bg-gray-50 min-h-screen py-8 px-2 sm:px-4 lg:px-12 xl:px-24">
            <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
                {/* Filter Sidebar */}
                <aside className="w-full lg:w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-6 h-fit mb-8 lg:mb-0 sticky self-start transition-shadow">
                    <h2 className="text-xl font-bold mb-4 text-black">Filter Products</h2>
                    {/* Only show main categories (level 0) */}
                    <div>
                        {level0Categories.map(cat => {
                            const catTag = slugify(cat.categorytag);
                            const isCatExpanded = expandedCategories.includes(catTag);
                            // Only get level 1s for this category
                            const level1s = isCatExpanded
                                ? level1Categories.filter(l1 => slugify(l1.categorytag) === catTag)
                                : [];
                            return (
                                <div key={cat.categoryid} className="mb-2">
                                    <div className="flex items-center justify-between">
                                        <Button
                                            variant={selectedCategory === catTag ? "red" : "outline"}
                                            className="px-3 py-1 rounded-full text-sm text-white bg-harvey-red flex-1 text-left hover:bg-harvey-red/90 uppercase"
                                            onClick={() => {
                                                setSelectedCategory(catTag);
                                                setSelectedLevel1(null);
                                                setSelectedShowcase(null);
                                            }}
                                        >
                                            {cat.categoryname}
                                        </Button>
                                        <button
                                            type="button"
                                            className="ml-2 text-harvey-red"
                                            onClick={() => toggleCategory(catTag)}
                                            aria-label={isCatExpanded ? "Collapse" : "Expand"}
                                        >
                                            {isCatExpanded ? <FaMinus /> : <FaPlus />}
                                        </button>
                                    </div>
                                    {/* Show level 1 only if this category is expanded */}
                                    {isCatExpanded && level1s.length > 0 && (
                                        <div className="ml-4 mt-2">
                                            {level1s.map(l1 => {
                                                const level1Tag = slugify(l1.categorytag);
                                                const isLevel1Expanded = expandedLevel1.includes(level1Tag);
                                                // Only get showcases for this level 1
                                                const showcasesInLevel1 = isLevel1Expanded
                                                    ? showcases.filter(s => slugify(s.showcasecategorytag) === level1Tag)
                                                    : [];
                                                // Group unique showcase names
                                                const uniqueShowcases = Array.from(
                                                    new Map(
                                                        showcasesInLevel1.map(s => [slugify(s.showcasename), s])
                                                    ).values()
                                                );
                                                return (
                                                    <div key={l1.categoryid} className="mb-2">
                                                        <div className="flex items-center justify-between">
                                                            <Button
                                                                variant={selectedLevel1 === level1Tag ? "red" : "outline"}
                                                                className="px-3 py-1 rounded-full text-sm text-black flex-1 text-left"
                                                                onClick={() => {
                                                                    setSelectedCategory(catTag);
                                                                    setSelectedLevel1(level1Tag);
                                                                    setSelectedShowcase(null);
                                                                }}
                                                            >
                                                                {l1.categoryname}
                                                            </Button>
                                                            <button
                                                                type="button"
                                                                className="ml-2 text-harvey-red"
                                                                onClick={() => toggleLevel1(level1Tag)}
                                                                aria-label={isLevel1Expanded ? "Collapse" : "Expand"}
                                                            >
                                                                {isLevel1Expanded ? <FaMinus /> : <FaPlus />}
                                                            </button>
                                                        </div>
                                                        {/* Show showcases only if this level 1 is expanded */}
                                                        {isLevel1Expanded && uniqueShowcases.length > 0 && (
                                                            <div className="ml-4 mt-2">
                                                                {uniqueShowcases.map(s => (
                                                                    <Button
                                                                        key={s.showcaseid}
                                                                        variant={selectedShowcase === slugify(s.showcasename) ? "red" : "outline"}
                                                                        className="px-3 py-1 rounded-full text-sm text-black w-full text-left mb-1"
                                                                        onClick={() => {
                                                                            setSelectedCategory(catTag);
                                                                            setSelectedLevel1(level1Tag);
                                                                            setSelectedShowcase(slugify(s.showcasename));
                                                                        }}
                                                                    >
                                                                        {s.showcasename}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {/* Reset filter button */}
                        <div className="mt-4">
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setSelectedLevel1(null);
                                    setSelectedShowcase(null);
                                    setExpandedCategories([]);
                                    setExpandedLevel1([]);
                                }}
                            >
                                Reset Filters
                            </Button>
                        </div>
                    </div>
                </aside>
                {/* Items Grid */}
                <main className="flex-1">
                    <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-harvey-red">All Products</h1>
                    {isLoadingItems ? (
                        <div className="text-center text-gray-500 py-12">Loading...</div>
                    ) : displayItems.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                            No items found. <SafeLink to="/products" className="text-harvey-red underline ml-2">Back to Products</SafeLink>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                            {displayItems.map(item => (
                                <div key={item.productid} className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center group hover:shadow-2xl transition">
                                    <img
                                        src={item.productimage || item.showcaseimageurl}
                                        alt={item.productname}
                                        className="w-32 h-32 object-cover rounded-xl mb-4 group-hover:scale-105 transition-transform"
                                    />
                                    <h3 className="text-lg text-black font-semibold mb-2 text-center">{item.productname}</h3>
                                    <p className="text-xs text-gray-500 mb-2 text-center">{item.productid}</p>
                                    <Button
                                        variant="red"
                                        className="mt-auto w-full"
                                        onClick={() => setQuoteItems(prev => [
                                            ...prev,
                                            { name: item.productname || item.showcaseitemname, quantity: 1 }
                                        ])}
                                    >
                                        Add to Quote List
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}